'use client';

import { ComponentPropsWithoutRef, FC, ReactNode } from 'react';

import { UrlObject } from 'url';

import classNames from 'classnames';
import NextLink from 'next/link';

import styles from './style.module.css';

type BaseProps = {
  variant?: 'primary' | 'secondary' | 'destructive';
  size?: 'S' | 'M' | 'L';
  children: ReactNode;
  isAnchor?: boolean;
  isDiv?: boolean;
  isWide?: boolean;
  isLoading?: boolean;
};

type NativeButtonProps = BaseProps &
  ComponentPropsWithoutRef<'button'> & {
    href?: never;
  };

type AnchorButtonProps = BaseProps &
  Omit<ComponentPropsWithoutRef<'a'>, 'href'> & {
    href: string | UrlObject;
    disabled?: boolean;
  };

type DivButtonProps = BaseProps & ComponentPropsWithoutRef<'div'>;

const Loading = () => (
  <div className={styles.loading}>
    <div className={styles.circle}></div>
  </div>
);

export type ButtonProps = NativeButtonProps | AnchorButtonProps;

export const Button: FC<ButtonProps> = ({
  variant = 'primary',
  size = 'M',
  isAnchor,
  isDiv,
  isWide,
  children,
  disabled,
  isLoading,
  ...props
}) => {
  /* eslint-disable @typescript-eslint/no-non-null-assertion */
  const className = classNames(styles.button, {
    [styles._primary!]: variant === 'primary',
    [styles._secondary!]: variant === 'secondary',
    [styles._destructive!]: variant === 'destructive',
    [styles._large!]: size === 'L',
    [styles._small!]: size === 'S',
    [styles._medium!]: size === 'M',
    [styles._wide!]: isWide,
  });

  const buttonClickHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isLoading || disabled) {
      e.preventDefault();
    } else {
      (props.onClick as NativeButtonProps['onClick'])?.(e);
    }
  };

  return isAnchor ? (
    <NextLink
      className={className}
      aria-disabled={disabled}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        if (disabled) {
          e.preventDefault();
          return;
        }
        (props as AnchorButtonProps).onClick?.(e);
      }}
      {...(props as AnchorButtonProps)}
    >
      <div className={styles.content}>{isLoading ? <Loading /> : children}</div>
    </NextLink>
  ) : isDiv ? (
    <div
      className={className}
      aria-disabled={disabled}
      {...(props as DivButtonProps)}
    >
      <div className={styles.content}>{isLoading ? <Loading /> : children}</div>
    </div>
  ) : (
    <button
      className={className}
      aria-disabled={disabled}
      {...(props as NativeButtonProps)}
      onClick={buttonClickHandler}
    >
      <div className={styles.content}>{isLoading ? <Loading /> : children}</div>
    </button>
  );
};
